import {
  etapesDepot,
  type EtapeDepot,
  type StatutPiece,
  type TransmissionPiece,
} from '@admin-pmvb/core'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { api, enBase64, ErreurApi, lire, requetes } from '../api'
import { Icone, type NomIcone } from './Icone'

// Envoyer une pièce, d'où que ce soit (Pièces, écriture du cabinet, tâche) : les fichiers choisis
// ou glissés ne partent pas seuls. Un récapitulatif dit ce qui va partir et où, « Envoyer » les
// envoie, puis le suivi montre chacun jusqu'au bout : reçu, lu, rattaché, chez le cabinet. Mes
// envois garde le même suivi (la photo y arrive aussi).

const ACCEPTES = /\.(pdf|xml)$/i

export const ACCEPT_FICHIERS = '.pdf,.xml,application/pdf,application/xml,text/xml'

/** Un fichier envoyé, suivi par sa pièce ; `nouvelle` faux : déjà reçu, on montre où il en est. */
type Envoye = { id: string; nom: string; nouvelle: boolean }

export type Depot = {
  prets: readonly File[]
  envoyes: readonly Envoye[]
  enCours: boolean
  erreur: string | null
  /** Prépare des fichiers : rien ne part avant `envoyer`. */
  choisir: (fichiers: readonly File[]) => void
  retirer: (index: number) => void
  annuler: () => void
  envoyer: (o: { entiteId?: string; indication?: string; tacheId?: string }) => Promise<void>
}

export function useDepot(): Depot {
  const client = useQueryClient()
  const [prets, setPrets] = useState<readonly File[]>([])
  const [envoyes, setEnvoyes] = useState<readonly Envoye[]>([])
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  return {
    prets,
    envoyes,
    enCours,
    erreur,
    choisir(fichiers) {
      if (enCours || fichiers.length === 0) return
      const retenus = fichiers.filter((f) => ACCEPTES.test(f.name))
      setErreur(retenus.length < fichiers.length ? 'Seuls les PDF et les XML sont acceptés.' : null)
      setPrets((p) => [...p, ...retenus])
    },
    retirer: (index) => setPrets((p) => p.filter((_, i) => i !== index)),
    annuler() {
      setPrets([])
      setErreur(null)
    },
    async envoyer(o) {
      if (enCours || prets.length === 0) return
      setEnCours(true)
      setErreur(null)
      const restent = [...prets]
      try {
        while (restent.length > 0) {
          const f = restent[0]!
          const r = await lire(
            api.pieces.$post({
              json: {
                nomFichier: f.name,
                contenuBase64: enBase64(await f.arrayBuffer()),
                ...(o.entiteId && { entiteId: o.entiteId }),
                ...(o.tacheId && { tacheId: o.tacheId }),
                ...(o.indication?.trim() && { indication: o.indication.trim() }),
              },
            }),
          )
          setEnvoyes((x) => [{ id: r.id, nom: f.name, nouvelle: r.nouvelle }, ...x])
          restent.shift()
          setPrets([...restent])
        }
        await client.invalidateQueries({ queryKey: ['pieces'] })
        await client.invalidateQueries({ queryKey: ['envois'] })
      } catch (e) {
        setErreur(
          e instanceof ErreurApi ? e.message : 'Envoi impossible : rien n’est perdu, réessayer.',
        )
      } finally {
        setEnCours(false)
      }
    },
  }
}

function taille(octets: number): string {
  return octets < 1024 * 1024
    ? `${Math.max(1, Math.round(octets / 1024))} ko`
    : `${(octets / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`
}

/**
 * Le récapitulatif avant l'envoi : les fichiers, ce qui les accompagne (`children` : entité,
 * indication ; ou `contexte`, une phrase), « Envoyer » et « Annuler ».
 */
export function RecapDepot({
  depot,
  contexte,
  bloque,
  surEnvoyer,
  children,
}: {
  depot: Depot
  contexte?: ReactNode
  /** Ce qui manque avant d'envoyer (entité à choisir). */
  bloque?: string | null
  surEnvoyer: () => void
  children?: ReactNode
}) {
  const zone = useRef<HTMLDivElement>(null)
  const present = depot.prets.length > 0
  // Fichier lâché n'importe où sur la page : le récapitulatif vient à l'écran.
  useEffect(() => {
    if (present) zone.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [present])
  if (!present) return depot.erreur ? <p className="alerte">{depot.erreur}</p> : null
  const n = depot.prets.length
  return (
    <div ref={zone} className="recap-depot" role="group" aria-label="Fichiers à envoyer">
      <strong>
        <Icone nom="outbox" /> {n === 1 ? 'Prêt à envoyer' : `${n} fichiers prêts à envoyer`}
      </strong>
      <ul className="recap-depot-fichiers">
        {depot.prets.map((f, i) => (
          <li key={`${f.name}-${i}`}>
            <Icone nom="description" taille={18} />
            <span className="recap-depot-nom">{f.name}</span>
            <span className="discret">{taille(f.size)}</span>
            <button
              type="button"
              className="bouton-icone"
              disabled={depot.enCours}
              aria-label={`Retirer ${f.name}`}
              title="Retirer"
              onClick={() => depot.retirer(i)}
            >
              <Icone nom="close" taille={18} />
            </button>
          </li>
        ))}
      </ul>
      {contexte && <p className="discret">{contexte}</p>}
      {children}
      {depot.erreur && <p className="alerte">{depot.erreur}</p>}
      {bloque && <p className="discret">{bloque}</p>}
      <div className="boutons">
        <button
          type="button"
          className="bouton bouton-principal"
          disabled={depot.enCours || !!bloque}
          onClick={surEnvoyer}
        >
          <Icone nom="send" />
          {depot.enCours ? 'Envoi…' : 'Envoyer'}
        </button>
        <button
          type="button"
          className="bouton-discret"
          disabled={depot.enCours}
          onClick={depot.annuler}
        >
          Annuler
        </button>
      </div>
    </div>
  )
}

const ICONES_ETAPE: Record<EtapeDepot['etat'], NomIcone> = {
  fait: 'check_circle',
  en_cours: 'sync',
  attention: 'error',
}

/** Les étapes d'un document envoyé : les mêmes ici et dans Mes envois. */
export function EtapesDepot({
  piece,
}: {
  piece: {
    statut: StatutPiece | null
    entite: string | null
    transmission: TransmissionPiece | null
    transmiseA: string | null
  }
}) {
  if (!piece.statut) return null
  return (
    <ol className="etapes-depot">
      {etapesDepot({ ...piece, statut: piece.statut }).map((e) => (
        <li key={e.cle} className={`etape-depot etape-${e.etat}`}>
          <Icone nom={ICONES_ETAPE[e.etat]} taille={16} plein={e.etat === 'fait'} />
          {e.texte}
        </li>
      ))}
    </ol>
  )
}

const INTERVALLE_SUIVI_MS = 5_000

/**
 * Ce qui vient de partir, et où chaque fichier en est, relu tant qu'une étape est en cours.
 * `surAvance` : à chaque étape franchie (la page rafraîchit ce qui en dépend).
 */
export function SuiviDepot({ depot, surAvance }: { depot: Depot; surAvance?: () => void }) {
  const fiches = useQueries({
    queries: depot.envoyes.map((e) => ({
      ...requetes.piece(e.id),
      refetchInterval: (q: { state: { data?: Parameters<typeof enCoursDe>[0] } }) =>
        q.state.data && !enCoursDe(q.state.data) ? false : INTERVALLE_SUIVI_MS,
    })),
  })
  const avance = fiches
    .map((f) => (f.data ? `${f.data.statut}/${f.data.transmission}` : ''))
    .join('|')
  const rappel = useRef(surAvance)
  rappel.current = surAvance
  useEffect(() => {
    if (avance) rappel.current?.()
  }, [avance])
  if (depot.envoyes.length === 0) return null
  const n = depot.envoyes.length
  return (
    <div className="suivi-depot" role="status">
      <strong className="suivi-depot-titre">
        <Icone nom="check_circle" plein />
        {n === 1 ? 'Pièce envoyée' : `${n} pièces envoyées`}
      </strong>
      <ul>
        {depot.envoyes.map((e, i) => {
          const p = fiches[i]?.data
          return (
            <li key={e.id}>
              <div className="suivi-depot-ligne">
                <span className="recap-depot-nom">{e.nom}</span>
                {!e.nouvelle && <span className="badge">Déjà reçue</span>}
                <Link to="/pieces/$id" params={{ id: e.id }}>
                  Voir la pièce
                </Link>
              </div>
              {p && (
                <EtapesDepot
                  piece={{
                    statut: p.statut,
                    entite: p.entite ?? null,
                    transmission: p.transmission,
                    transmiseA: p.transmiseA,
                  }}
                />
              )}
            </li>
          )
        })}
      </ul>
      <Link to="/envois" className="discret">
        Tous mes envois
      </Link>
    </div>
  )
}

function enCoursDe(p: {
  statut: StatutPiece
  entite?: string | null
  transmission: TransmissionPiece | null
  transmiseA: string | null
}): boolean {
  return (
    etapesDepot({
      statut: p.statut,
      entite: p.entite ?? null,
      transmission: p.transmission,
      transmiseA: p.transmiseA,
    }).at(-1)?.etat === 'en_cours'
  )
}
